<template>
  <!-- Teleport damit die Badges safe über allem liegen -->
  <Teleport to="body">
    <div class="dtid-overlay-root" aria-hidden="true">
      <div
        v-for="b in badges"
        :key="b.key"
        class="dtid-badge"
        :style="{
          top: b.top + 'px',
          left: b.left + 'px',
        }"
      >
        {{ b.testId }}
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

type Badge = {
  key: string;
  testId: string;
  top: number;
  left: number;
};

type Options = {
  intervalMs?: number;
  selector?: string;
  // Optional: offset falls du die Badge nicht exakt auf rect.top/left willst
  offsetX?: number;
  offsetY?: number;
  // Optional: nur sichtbare Elemente anzeigen
  onlyVisible?: boolean;
};

const props = withDefaults(defineProps<Options>(), {
  intervalMs: 500,
  selector: "[data-testid]",
  offsetX: 0,
  offsetY: 0,
  onlyVisible: false,
});

const badges = ref<Badge[]>([]);

let timer: number | undefined;

function isElementVisible(rect: DOMRect) {
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0;
}

function updateBadges() {
  const els = Array.from(document.querySelectorAll<HTMLElement>(props.selector));

  const next: Badge[] = [];

  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const testId = el.getAttribute("data-testid");
    if (!testId) continue;

    const rect = el.getBoundingClientRect();
    if (props.onlyVisible && !isElementVisible(rect)) continue;

    // Position relativ zur Seite (nicht viewport)
    const top = window.scrollY + rect.top + props.offsetY;
    const left = window.scrollX + rect.left + props.offsetX;

    // key: stabil-ish, aber ohne globale IDs. Wenn du willst, kann man hier auch dataset-hash + index machen.
    const key = `${testId}::${Math.round(top)}::${Math.round(left)}::${i}`;

    next.push({ key, testId, top, left });
  }

  badges.value = next;
}

function onScrollOrResize() {
  // Kein 2000x pro Scroll-Event, also minimal throttlen via rAF
  requestAnimationFrame(updateBadges);
}

onMounted(() => {
  updateBadges();

  timer = window.setInterval(updateBadges, props.intervalMs);

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);

  // Bonus: falls DOM dynamisch (React/Vue Apps) sich dauernd ändert,
  // ist polling eh schon da. Aber MutationObserver macht’s schneller für Änderungen.
  // Wenn du keinen Bock auf Observer hast, lösch einfach den Block.
  const mo = new MutationObserver(() => updateBadges());
  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-testid", "style", "class"],
  });

  onBeforeUnmount(() => {
    mo.disconnect();
  });
});

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);

  window.removeEventListener("scroll", onScrollOrResize);
  window.removeEventListener("resize", onScrollOrResize);
});
</script>

<style scoped>
/* Root ist nur ein Container */
.dtid-overlay-root {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483647; /* maximaler "ich bin wichtiger als du"-Wert */
}

/* Badge selbst wird absolut zur Seite positioniert */
.dtid-badge {
  position: absolute;
  opacity: 0.9;
  background: rgba(255, 0, 0, 0.85);
  color: #fff;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  line-height: 1.2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
